import React, { FC, useEffect, useMemo, useRef, useState } from "react";
import {
  alpha,
  Box,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Slider,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import CenterFocusStrongIcon from "@mui/icons-material/CenterFocusStrong";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { collection, onSnapshot, orderBy, query } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../store";
import TablesOccupancyHeatmap from "./TablesOccupancyHeatmap";
import TablesOccupancyNow from "./TablesOccupancyNow";
import { buildAdminMatchsUrl } from "./occupancyNavigation";
import {
  fetchBoards,
  fetchRounds,
  getBoards,
  getRounds,
  getTournoi,
} from "../../../features/tournoi/tournoiSlice";
import {
  fetchTables,
  getTables,
} from "../../../features/matchs/matchsSlice";
import firestoreInstance from "../../../services/firestoreInstance";
import { Matchs, Table } from "../../../types";
import { calendarDayKey } from "../../../features/player/playerHelper";
import { currentDate } from "../../../helpers/currentDate";
import { formatDuration } from "../../../utils/dateUtils";

const TABLE_LABEL_WIDTH_PX = 64;
const BOARD_TOUR_LABEL_WIDTH_PX = 96;
const BOARD_LABEL_WIDTH_PX = 88;
const ROW_HEIGHT_PX = 38;
const LANE_HEIGHT_PX = 28;
const BASE_PX_PER_HOUR = 128;
const MIN_ZOOM = 0.5;
const MAX_ZOOM = 3.5;
const ZOOM_STEP = 0.25;
const RANGE_PAD_MS = 15 * 60_000;

type ViewMode = "table" | "boardTour" | "board" | "heatmap" | "now";

type OccupancyBlock = {
  match: Matchs;
  /** Toujours `dateLancement` — jamais `dateCreation`. */
  start: Date;
  /** `dateFin`, ou « maintenant » / fin de journée si ENCOURS sans fin. */
  end: Date;
};

type LanedBlock = OccupancyBlock & { lane: number };

type TimeRange = {
  startMs: number;
  endMs: number;
};

type TableGroup = {
  key: string;
  label: string;
  tables: Table[];
};

type BoardTourRow = {
  key: string;
  boardId: string;
  boardName: string;
  tourId: string;
  tourLabel: string;
  tourOrdre: number;
  blocks: LanedBlock[];
  laneCount: number;
};

type BoardTourGroup = {
  key: string;
  label: string;
  rows: BoardTourRow[];
};

/** Un bloc = tous les matchs d'un tour pour un tableau (plage min→max). */
type TourSpan = {
  tourId: string;
  tourLabel: string;
  tourOrdre: number;
  start: Date;
  end: Date;
  matchCount: number;
  liveCount: number;
  tableIds: number[];
  blocks: OccupancyBlock[];
  /** Voie d'affichage si chevauchement avec un autre tour. */
  lane: number;
  /** Chevauche au moins un autre tour sur ce tableau. */
  overlaps: boolean;
};

type BoardRow = {
  key: string;
  boardId: string;
  boardName: string;
  boardLabel: string;
  tours: TourSpan[];
  laneCount: number;
};

function formatTime(date: Date): string {
  return date.toLocaleTimeString("fr-FR", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatDayLabel(dateKey: string): string {
  const [year, month, day] = dateKey.split("-").map(Number);
  const date = new Date(year, month - 1, day);
  return date.toLocaleDateString("fr-FR", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function parseDayParts(dateKey: string): {
  year: number;
  month: number;
  day: number;
} {
  const [year, month, day] = dateKey.split("-").map(Number);
  return { year, month, day };
}

function dayStartMs(dateKey: string): number {
  const { year, month, day } = parseDayParts(dateKey);
  return new Date(year, month - 1, day, 0, 0, 0, 0).getTime();
}

function playerNames(match: Matchs): string {
  const names = (match.matchsPlayerParticipations ?? [])
    .slice()
    .sort((a, b) => (a.ordre ?? 0) - (b.ordre ?? 0))
    .map((p) => {
      const player = p.players;
      if (!player) return null;
      return `${player.name ?? ""} ${player.surname ?? ""}`.trim();
    })
    .filter((name): name is string => Boolean(name));
  return names.length > 0 ? names.join(" · ") : "Joueurs non renseignés";
}

function playerCount(match: Matchs): number {
  return match.matchsPlayerParticipations?.length ?? 0;
}

function statusLabel(status: Matchs["status"]): string {
  switch (status) {
    case "ENCOURS":
      return "En cours";
    case "TERMINE":
      return "Terminé";
    case "LIBERE":
      return "Libéré";
    case "DOUBLON":
      return "Doublon";
    case "CREE":
      return "Créé";
    default:
      return status ?? "—";
  }
}

function durationSeconds(start: Date, end: Date): number {
  return Math.max(0, (end.getTime() - start.getTime()) / 1000);
}

function percentBetween(
  value: number,
  rangeStart: number,
  rangeEnd: number
): number {
  if (rangeEnd <= rangeStart) return 0;
  return ((value - rangeStart) / (rangeEnd - rangeStart)) * 100;
}

/** Aligne sur le début d'heure (ex. 07:54 → 07:00). */
function floorToHour(ms: number): number {
  const d = new Date(ms);
  d.setMinutes(0, 0, 0);
  return d.getTime();
}

/** Aligne sur l'heure suivante si minutes > 0 (ex. 13:28 → 14:00). */
function ceilToHour(ms: number): number {
  const d = new Date(ms);
  if (d.getMinutes() === 0 && d.getSeconds() === 0 && d.getMilliseconds() === 0) {
    return d.getTime();
  }
  d.setMinutes(0, 0, 0);
  d.setHours(d.getHours() + 1);
  return d.getTime();
}

function computeFocusedRange(
  dateKey: string,
  blocks: OccupancyBlock[]
): TimeRange {
  const { year, month, day } = parseDayParts(dateKey);
  const minDay = dayStartMs(dateKey);
  const nextMidnight = new Date(year, month - 1, day + 1, 0, 0, 0, 0).getTime();

  if (blocks.length === 0) {
    return {
      startMs: new Date(year, month - 1, day, 8, 0, 0, 0).getTime(),
      endMs: new Date(year, month - 1, day, 18, 0, 0, 0).getTime(),
    };
  }

  let min = Number.POSITIVE_INFINITY;
  let max = Number.NEGATIVE_INFINITY;
  for (const block of blocks) {
    min = Math.min(min, block.start.getTime());
    max = Math.max(max, block.end.getTime());
  }

  // Début = heure du 1er match (pas de marge avant → évite une colonne 7h vide)
  // Fin = heure suivante après le dernier match (+ petite marge)
  let startMs = floorToHour(Math.max(minDay, min));
  let endMs = Math.min(nextMidnight, ceilToHour(max + RANGE_PAD_MS));

  if (endMs - startMs < 60 * 60_000) {
    endMs = Math.min(nextMidnight, startMs + 60 * 60_000);
  }

  if (endMs <= startMs) {
    endMs = Math.min(nextMidnight, startMs + 60 * 60_000);
  }

  return { startMs, endMs };
}

function buildHourTicks(range: TimeRange): number[] {
  const ticks: number[] = [];
  const start = new Date(range.startMs);
  start.setMinutes(0, 0, 0);
  if (start.getTime() < range.startMs) {
    start.setHours(start.getHours() + 1);
  }
  for (let t = start.getTime(); t <= range.endMs; t += 60 * 60_000) {
    ticks.push(t);
  }
  if (ticks.length === 0) ticks.push(range.startMs);
  return ticks;
}

function groupTablesByRoom(tables: Table[]): TableGroup[] {
  const sorted = [...tables].sort((a, b) => {
    if (a.roomId !== b.roomId) return a.roomId - b.roomId;
    return a.tableId - b.tableId;
  });
  const groups: TableGroup[] = [];
  for (const table of sorted) {
    const key = String(table.roomId);
    const last = groups[groups.length - 1];
    if (last && last.key === key) {
      last.tables.push(table);
    } else {
      groups.push({
        key,
        label: table.roomName?.trim() || `Salle ${table.roomId}`,
        tables: [table],
      });
    }
  }
  return groups;
}

/**
 * Codes courts stables — jamais de troncature « Po… ».
 * Ex. Poules→P, 16e→16, quart→1/4, finale→F
 */
function shortTourCode(label: string): string {
  const normalized = label.trim().toLowerCase();
  if (normalized.includes("poule")) return "P";
  if (normalized.includes("64")) return "64";
  if (normalized.includes("32")) return "32";
  if (normalized.includes("16")) return "16";
  if (normalized.includes("8e") || normalized.includes("huitième")) return "8";
  if (normalized.includes("quart")) return "1/4";
  if (normalized.includes("demi")) return "1/2";
  if (normalized.includes("finale")) return "F";
  const compact = label.trim();
  return compact.length <= 4 ? compact : compact.slice(0, 3);
}

/** Libellé compact dans la barre : « C·P », « E·16 » — tient sans ellipsis. */
function barInnerLabel(boardName: string | undefined, tourLabel: string): string {
  const board = (boardName ?? "").trim() || "?";
  return `${board}·${shortTourCode(tourLabel)}`;
}

/** Empile les matchs qui se chevauchent sur des « voies » (vue tableau/tour). */
function assignLanes(blocks: OccupancyBlock[]): {
  blocks: LanedBlock[];
  laneCount: number;
} {
  const sorted = [...blocks].sort(
    (a, b) => a.start.getTime() - b.start.getTime()
  );
  const laneEnds: number[] = [];
  const laned: LanedBlock[] = [];

  for (const block of sorted) {
    let lane = laneEnds.findIndex((end) => end <= block.start.getTime());
    if (lane === -1) {
      lane = laneEnds.length;
      laneEnds.push(block.end.getTime());
    } else {
      laneEnds[lane] = block.end.getTime();
    }
    laned.push({ ...block, lane });
  }

  return { blocks: laned, laneCount: Math.max(1, laneEnds.length) };
}

function rangesOverlap(
  aStart: number,
  aEnd: number,
  bStart: number,
  bEnd: number
): boolean {
  return aStart < bEnd && bStart < aEnd;
}

/** Empile les blocs-tours qui se chevauchent (vue Par tableau). */
function assignTourSpanLanes(
  tours: Omit<TourSpan, "lane" | "overlaps">[]
): { tours: TourSpan[]; laneCount: number } {
  const sorted = [...tours].sort(
    (a, b) => a.start.getTime() - b.start.getTime()
  );
  const laneEnds: number[] = [];
  const withLane: TourSpan[] = [];

  for (const tour of sorted) {
    let lane = laneEnds.findIndex((end) => end <= tour.start.getTime());
    if (lane === -1) {
      lane = laneEnds.length;
      laneEnds.push(tour.end.getTime());
    } else {
      laneEnds[lane] = tour.end.getTime();
    }
    withLane.push({ ...tour, lane, overlaps: false });
  }

  for (let i = 0; i < withLane.length; i += 1) {
    for (let j = i + 1; j < withLane.length; j += 1) {
      if (
        rangesOverlap(
          withLane[i].start.getTime(),
          withLane[i].end.getTime(),
          withLane[j].start.getTime(),
          withLane[j].end.getTime()
        )
      ) {
        withLane[i].overlaps = true;
        withLane[j].overlaps = true;
      }
    }
  }

  return { tours: withLane, laneCount: Math.max(1, laneEnds.length) };
}

const LegendSwatch: FC<{
  color: string;
  label: string;
  threePlayers?: boolean;
  accent?: string;
}> = ({ color, label, threePlayers, accent }) => (
  <Stack direction="row" spacing={0.75} alignItems="center">
    <Box
      sx={{
        position: "relative",
        width: 24,
        height: 14,
        borderRadius: 0.75,
        bgcolor: color,
        flexShrink: 0,
        overflow: "hidden",
        boxShadow: `inset 0 0 0 1px ${alpha("#000", 0.15)}`,
        ...(threePlayers && accent
          ? {
              "&::before": {
                content: '""',
                position: "absolute",
                left: 0,
                top: 0,
                bottom: 0,
                width: 2,
                bgcolor: accent,
              },
            }
          : {}),
      }}
    />
    <Typography
      variant="caption"
      sx={{ color: "text.secondary", fontWeight: 500 }}
    >
      {label}
    </Typography>
  </Stack>
);

const MatchTooltipContent: FC<{
  match: Matchs;
  start: Date;
  end: Date;
  boardLabel: string;
  tourLabel: string;
}> = ({ match, start, end, boardLabel, tourLabel }) => {
  const nb = playerCount(match);
  const isLive = match.status === "ENCOURS" && !(match.dateFin instanceof Date);

  return (
    <Box sx={{ p: 0.75, maxWidth: 320 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75 }}>
        {playerNames(match)}
      </Typography>
      <Stack spacing={0.35}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          Tableau {boardLabel}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {tourLabel}
        </Typography>
        <Typography variant="body2">
          {nb} joueur{nb > 1 ? "s" : ""} · {statusLabel(match.status)}
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          Lancé {formatTime(start)}
          {" → "}
          {isLive ? "en cours" : `Fin ${formatTime(end)}`}
        </Typography>
        <Typography variant="caption" sx={{ opacity: 0.85 }}>
          Durée {formatDuration(durationSeconds(start, end))}
          {match.tableId != null ? ` · Table ${match.tableId}` : ""}
        </Typography>
      </Stack>
    </Box>
  );
};

const TourSpanTooltipContent: FC<{
  boardLabel: string;
  tour: TourSpan;
}> = ({ boardLabel, tour }) => (
  <Box sx={{ p: 0.75, maxWidth: 320 }}>
    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75 }}>
      {boardLabel}
    </Typography>
    <Stack spacing={0.35}>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {tour.tourLabel}
      </Typography>
      {tour.overlaps && (
        <Typography variant="body2" sx={{ color: "#F0C14A", fontWeight: 700 }}>
          Chevauchement avec un autre tour
        </Typography>
      )}
      <Typography variant="body2">
        {tour.matchCount} match{tour.matchCount > 1 ? "s" : ""}
        {tour.liveCount > 0 ? ` · ${tour.liveCount} en cours` : ""}
      </Typography>
      <Typography variant="body2" sx={{ mt: 0.5 }}>
        {formatTime(tour.start)} → {formatTime(tour.end)}
      </Typography>
      <Typography variant="caption" sx={{ opacity: 0.85 }}>
        Durée {formatDuration(durationSeconds(tour.start, tour.end))}
        {tour.tableIds.length > 0
          ? ` · Tables ${tour.tableIds.join(", ")}`
          : ""}
      </Typography>
    </Stack>
  </Box>
);

const TablesOccupancy: FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const tournoi = useAppSelector(getTournoi);
  const boards = useAppSelector(getBoards);
  const rounds = useAppSelector(getRounds);
  const tables = useAppSelector(getTables);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const openMatchInAdmin = (match: Matchs) => {
    navigate(
      buildAdminMatchsUrl({
        matchId: match.id != null ? String(match.id) : undefined,
        tableId: match.tableId,
        tableauId:
          match.tableauId != null ? String(match.tableauId) : undefined,
        tourId: match.tourId != null ? String(match.tourId) : undefined,
      })
    );
  };

  const openBoardTourInAdmin = (tableauId: string, tourId: string) => {
    navigate(buildAdminMatchsUrl({ tableauId, tourId }));
  };

  const [matches, setMatches] = useState<Matchs[]>([]);
  const [loadingMatches, setLoadingMatches] = useState(true);
  const [now, setNow] = useState(() => new Date());
  const [selectedDate, setSelectedDate] = useState(
    () => calendarDayKey(currentDate())
  );
  const [zoom, setZoom] = useState(1);
  const [viewRange, setViewRange] = useState<TimeRange | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>("now");

  const isDark = theme.palette.mode === "dark";
  const isTimelineView =
    viewMode === "table" ||
    viewMode === "boardTour" ||
    viewMode === "board";

  const labelWidthPx =
    viewMode === "table"
      ? TABLE_LABEL_WIDTH_PX
      : viewMode === "boardTour"
        ? BOARD_TOUR_LABEL_WIDTH_PX
        : BOARD_LABEL_WIDTH_PX;

  /** Matchs ENCOURS pour la vue « Maintenant » (pas filtrés par journée). */
  const liveNowBlocks = useMemo(() => {
    const blocks: OccupancyBlock[] = [];
    for (const match of matches) {
      if (match.status !== "ENCOURS" || match.tableId == null) continue;
      if (!(match.dateLancement instanceof Date)) continue;
      const start = match.dateLancement;
      const end =
        match.dateFin instanceof Date
          ? match.dateFin
          : new Date(start.getTime() + 28 * 60_000);
      blocks.push({ match, start, end });
    }
    return blocks;
  }, [matches]);

  // Palette dédiée : barres assez claires pour contraster fond sombre + texte blanc
  const colorTermine = isDark ? "#5BA3E0" : theme.palette.primary.main;
  const colorEncours = isDark ? "#4CC38A" : theme.palette.success.main;
  const colorHover = isDark ? "#7BB8EA" : theme.palette.primary.dark;
  const colorThreeAccent = isDark ? "#F0C14A" : "#C9920A";
  const barLabelColor = "#FFFFFF";

  useEffect(() => {
    dispatch(fetchBoards());
    dispatch(fetchRounds());
    dispatch(fetchTables());
  }, [dispatch]);

  useEffect(() => {
    if (!tournoi?.id) return;

    const collectionName = tournoi.statsCollectionName || "matchs";
    const matchsCollectionRef =
      collectionName === "matchs"
        ? collection(firestoreInstance, "tournois", tournoi.id, "matchs")
        : collection(firestoreInstance, collectionName);
    const q = query(matchsCollectionRef, orderBy("dateCreation"));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const next = snapshot.docs.map((docSnap) => {
          const data = docSnap.data();
          return {
            id: docSnap.id,
            ...data,
            dateCreation: data.dateCreation?.toDate?.() ?? data.dateCreation,
            dateLancement:
              data.dateLancement?.toDate?.() ?? data.dateLancement,
            dateFin: data.dateFin?.toDate?.() ?? data.dateFin,
          } as Matchs;
        });
        setMatches(next);
        setLoadingMatches(false);
      },
      (error) => {
        console.error("Occupation: écoute matchs impossible", error);
        setMatches([]);
        setLoadingMatches(false);
      }
    );

    return () => unsubscribe();
  }, [tournoi?.id, tournoi?.statsCollectionName]);

  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 30_000);
    return () => window.clearInterval(timer);
  }, []);

  const availableDates = useMemo(() => {
    const dates = new Set<string>();
    boards.forEach((board) => {
      if (board.date) dates.add(calendarDayKey(board.date));
    });
    matches.forEach((match) => {
      if (match.dateLancement instanceof Date) {
        dates.add(calendarDayKey(match.dateLancement));
      }
    });
    return Array.from(dates).sort();
  }, [boards, matches]);

  useEffect(() => {
    if (availableDates.length === 0) return;
    if (!availableDates.includes(selectedDate)) {
      setSelectedDate(availableDates[0]);
    }
  }, [availableDates, selectedDate]);

  const tableGroups = useMemo(() => groupTablesByRoom(tables), [tables]);

  const boardsById = useMemo(() => {
    const map = new Map<string, (typeof boards)[number]>();
    boards.forEach((board) => {
      if (board.id != null) map.set(String(board.id), board);
    });
    return map;
  }, [boards]);

  const roundsById = useMemo(() => {
    const map = new Map<string, (typeof rounds)[number]>();
    rounds.forEach((round) => {
      if (round.id != null) map.set(String(round.id), round);
    });
    return map;
  }, [rounds]);

  const dayBlocks = useMemo(() => {
    const blocks: OccupancyBlock[] = [];

    for (const match of matches) {
      if (match.tableId == null || !(match.dateLancement instanceof Date)) {
        continue;
      }
      if (calendarDayKey(match.dateLancement) !== selectedDate) {
        continue;
      }

      const start = match.dateLancement;
      // Jour courant : s'étend jusqu'à maintenant. Jour passé : barre ~28 min (évite d'aller à minuit).
      const openEndedEnd =
        calendarDayKey(now) === selectedDate
          ? now
          : new Date(start.getTime() + 28 * 60_000);
      const end =
        match.dateFin instanceof Date
          ? match.dateFin
          : match.status === "ENCOURS"
            ? openEndedEnd
            : // TERMINE (ou autre) sans dateFin : barre minimale pour rester visible
              new Date(start.getTime() + 28 * 60_000);

      if (end.getTime() <= start.getTime()) continue;
      blocks.push({ match, start, end });
    }

    return blocks;
  }, [matches, selectedDate, now]);

  const occupancyByTable = useMemo(() => {
    const map = new Map<number, OccupancyBlock[]>();
    for (const block of dayBlocks) {
      const tableId = block.match.tableId;
      if (tableId == null) continue;
      const list = map.get(tableId) ?? [];
      list.push(block);
      map.set(tableId, list);
    }
    for (const [, list] of Array.from(map.entries())) {
      list.sort((a, b) => a.start.getTime() - b.start.getTime());
    }
    return map;
  }, [dayBlocks]);

  const boardTourGroups = useMemo((): BoardTourGroup[] => {
    const byKey = new Map<string, OccupancyBlock[]>();
    for (const block of dayBlocks) {
      const boardId = String(block.match.tableauId ?? "");
      const tourId = String(block.match.tourId ?? "");
      const key = `${boardId}::${tourId}`;
      const list = byKey.get(key) ?? [];
      list.push(block);
      byKey.set(key, list);
    }

    const rows: BoardTourRow[] = Array.from(byKey.entries()).map(
      ([key, blocks]) => {
        const [boardId, tourId] = key.split("::");
        const board = boardsById.get(boardId);
        const tour = roundsById.get(tourId);
        const laned = assignLanes(blocks);
        return {
          key,
          boardId,
          boardName: board?.name?.trim() || "?",
          tourId,
          tourLabel: tour?.label ?? "Tour inconnu",
          tourOrdre: tour?.ordre ?? 999,
          blocks: laned.blocks,
          laneCount: laned.laneCount,
        };
      }
    );

    rows.sort((a, b) => {
      const byBoard = a.boardName.localeCompare(b.boardName, "fr");
      if (byBoard !== 0) return byBoard;
      return a.tourOrdre - b.tourOrdre;
    });

    const groups: BoardTourGroup[] = [];
    for (const row of rows) {
      const last = groups[groups.length - 1];
      if (last && last.key === row.boardId) {
        last.rows.push(row);
      } else {
        const board = boardsById.get(row.boardId);
        groups.push({
          key: row.boardId,
          label: board
            ? `${board.name}${board.label ? ` · ${board.label}` : ""}`
            : `Tableau ${row.boardName}`,
          rows: [row],
        });
      }
    }
    return groups;
  }, [dayBlocks, boardsById, roundsById]);

  /** Une ligne par tableau ; chaque bloc = plage d'un tour (sans regroupement salle). */
  const boardRows = useMemo((): BoardRow[] => {
    const byBoard = new Map<string, Map<string, OccupancyBlock[]>>();

    for (const block of dayBlocks) {
      const boardId = String(block.match.tableauId ?? "");
      const tourId = String(block.match.tourId ?? "");
      if (!byBoard.has(boardId)) byBoard.set(boardId, new Map());
      const byTour = byBoard.get(boardId)!;
      const list = byTour.get(tourId) ?? [];
      list.push(block);
      byTour.set(tourId, list);
    }

    const rows: BoardRow[] = [];
    for (const [boardId, byTour] of Array.from(byBoard.entries())) {
      const board = boardsById.get(boardId);
      const boardName = board?.name?.trim() || "?";
      const rawTours = Array.from(byTour.entries()).map(([tourId, blocks]) => {
        const tour = roundsById.get(tourId);
        let startMs = Number.POSITIVE_INFINITY;
        let endMs = Number.NEGATIVE_INFINITY;
        const tableIds = new Set<number>();
        let liveCount = 0;
        for (const b of blocks) {
          startMs = Math.min(startMs, b.start.getTime());
          endMs = Math.max(endMs, b.end.getTime());
          if (b.match.tableId != null) tableIds.add(b.match.tableId);
          if (b.match.status === "ENCOURS") liveCount += 1;
        }
        return {
          tourId,
          tourLabel: tour?.label ?? "Tour inconnu",
          tourOrdre: tour?.ordre ?? 999,
          start: new Date(startMs),
          end: new Date(endMs),
          matchCount: blocks.length,
          liveCount,
          tableIds: Array.from(tableIds).sort((a, b) => a - b),
          blocks,
        };
      });
      rawTours.sort(
        (a, b) =>
          a.tourOrdre - b.tourOrdre || a.start.getTime() - b.start.getTime()
      );
      const laned = assignTourSpanLanes(rawTours);
      rows.push({
        key: boardId,
        boardId,
        boardName,
        boardLabel: board
          ? `${board.name}${board.label ? ` · ${board.label}` : ""}`
          : `Tableau ${boardName}`,
        tours: laned.tours,
        laneCount: laned.laneCount,
      });
    }

    rows.sort((a, b) => a.boardName.localeCompare(b.boardName, "fr"));
    return rows;
  }, [dayBlocks, boardsById, roundsById]);

  const allBlocks = dayBlocks;

  const focusedRange = useMemo(
    () => computeFocusedRange(selectedDate, allBlocks),
    [selectedDate, allBlocks]
  );

  const matchesSignature = useMemo(
    () =>
      allBlocks
        .map(
          (b) =>
            `${String(b.match.id)}:${b.start.getTime()}:${
              b.match.dateFin instanceof Date ? b.end.getTime() : "open"
            }`
        )
        .sort()
        .join("|"),
    [allBlocks]
  );

  const allBlocksRef = useRef(allBlocks);
  allBlocksRef.current = allBlocks;

  useEffect(() => {
    setViewRange(computeFocusedRange(selectedDate, allBlocksRef.current));
    setZoom(1);
  }, [selectedDate, matchesSignature]);

  const activeRange = viewRange ?? focusedRange;
  const rangeDurationMs = Math.max(activeRange.endMs - activeRange.startMs, 1);
  const timelineWidthPx =
    (rangeDurationMs / (60 * 60_000)) * BASE_PX_PER_HOUR * zoom;
  const hourTicks = useMemo(() => buildHourTicks(activeRange), [activeRange]);

  const nowPercent = useMemo(() => {
    if (calendarDayKey(now) !== selectedDate) return null;
    if (
      now.getTime() < activeRange.startMs ||
      now.getTime() > activeRange.endMs
    ) {
      return null;
    }
    return percentBetween(
      now.getTime(),
      activeRange.startMs,
      activeRange.endMs
    );
  }, [now, selectedDate, activeRange]);

  const stats = useMemo(() => {
    const tablesUsed = occupancyByTable.size;
    const boardTourPairs = boardTourGroups.reduce(
      (acc, g) => acc + g.rows.length,
      0
    );
    const liveCount = allBlocks.filter((b) => b.match.status === "ENCOURS")
      .length;
    const threePlayerCount = allBlocks.filter((b) => playerCount(b.match) >= 3)
      .length;
    return {
      matchCount: allBlocks.length,
      tablesUsed,
      boardTourPairs,
      boardCount: boardRows.length,
      liveCount,
      threePlayerCount,
    };
  }, [allBlocks, occupancyByTable, boardTourGroups, boardRows]);

  const handleZoom = (nextZoom: number) => {
    setZoom(Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, nextZoom)));
  };

  const handleResetFocus = () => {
    setViewRange(focusedRange);
    setZoom(1);
  };

  const handleWheelZoom = (event: React.WheelEvent) => {
    if (!(event.ctrlKey || event.metaKey)) return;
    event.preventDefault();
    handleZoom(zoom + (event.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP));
  };

  const loading = loadingMatches || !tournoi;

  const chromeBg = isDark ? "#141820" : "#F4F6F8";
  const trackBg = isDark ? "#1A1F28" : "#FFFFFF";
  const headerBg = isDark ? "#222833" : "#EEF1F5";
  const labelColBg = isDark ? "#1E2430" : "#F0F3F7";
  const rowEvenBg = isDark ? "#1A1F28" : "#FFFFFF";
  const rowOddBg = isDark ? "#161B24" : "#F7F9FB";
  const groupBg = isDark ? "#2A3342" : "#E4EAF1";
  const gridLine = isDark
    ? alpha("#FFFFFF", 0.07)
    : alpha("#0B1F33", 0.08);
  const gridLineStrong = isDark
    ? alpha("#FFFFFF", 0.14)
    : alpha("#0B1F33", 0.14);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="240px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: { xs: 2, md: 3 },
        maxWidth: 1440,
        mx: "auto",
      }}
    >
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", md: "flex-end" }}
        spacing={2}
        sx={{ mb: 2.5 }}
      >
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: -0.3 }}>
            Occupation des tables
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Carte spatio-temporelle des tables — le détail opérationnel reste
            sur Gérer les matchs
            {tournoi?.label ? ` · ${tournoi.label}` : ""}
          </Typography>
        </Box>

        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Chip
            size="small"
            label={`${stats.matchCount} match${stats.matchCount > 1 ? "s" : ""}`}
            variant="outlined"
          />
          {viewMode === "now" ? (
            <Chip
              size="small"
              color="success"
              label={`${liveNowBlocks.length} table${liveNowBlocks.length > 1 ? "s" : ""} en cours`}
            />
          ) : (
            <>
              <Chip
                size="small"
                label={
                  viewMode === "table" || viewMode === "heatmap"
                    ? `${stats.tablesUsed} table${stats.tablesUsed > 1 ? "s" : ""}`
                    : viewMode === "boardTour"
                      ? `${stats.boardTourPairs} couple${stats.boardTourPairs > 1 ? "s" : ""} tableau/tour`
                      : `${stats.boardCount} tableau${stats.boardCount > 1 ? "x" : ""}`
                }
                variant="outlined"
              />
              {stats.liveCount > 0 && (
                <Chip
                  size="small"
                  color="success"
                  label={`${stats.liveCount} en cours`}
                />
              )}
              {stats.threePlayerCount > 0 && viewMode !== "heatmap" && (
                <Chip
                  size="small"
                  variant="outlined"
                  label={`${stats.threePlayerCount} à 3 joueurs`}
                />
              )}
            </>
          )}
        </Stack>
      </Stack>

      <Tabs
        value={viewMode}
        onChange={(_, value: ViewMode) => setViewMode(value)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 1, minHeight: 40 }}
      >
        <Tab
          value="now"
          label="Maintenant"
          sx={{ textTransform: "none", fontWeight: 600, minHeight: 40 }}
        />
        <Tab
          value="table"
          label="Par table"
          sx={{ textTransform: "none", fontWeight: 600, minHeight: 40 }}
        />
        <Tab
          value="board"
          label="Par tableau"
          sx={{ textTransform: "none", fontWeight: 600, minHeight: 40 }}
        />
        <Tab
          value="boardTour"
          label="Détail des tours"
          sx={{ textTransform: "none", fontWeight: 600, minHeight: 40 }}
        />
        <Tab
          value="heatmap"
          label="Saturation"
          sx={{ textTransform: "none", fontWeight: 600, minHeight: 40 }}
        />
      </Tabs>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
        {viewMode === "now" &&
          "Snapshot live des tables — clic pour ouvrir le match dans Gérer les matchs"}
        {viewMode === "table" &&
          "Ops salle : occupation table par table — clic sur un match → Gérer les matchs"}
        {viewMode === "board" &&
          "Avancement temporel par tableau (1 bloc = 1 tour)"}
        {viewMode === "boardTour" &&
          "Diagnostic d’un tour : matchs en parallèle sur plusieurs tables"}
        {viewMode === "heatmap" &&
          "Charge globale : % de tables occupées dans le temps"}
      </Typography>

      {viewMode !== "now" && (
        <Stack
          direction={{ xs: "column", lg: "row" }}
          spacing={2}
          alignItems={{ xs: "stretch", lg: "center" }}
          justifyContent="space-between"
          sx={{
            mb: 2,
            px: 2,
            py: 1.5,
            borderRadius: 2,
            bgcolor: chromeBg,
            border: 1,
            borderColor: isDark ? alpha("#fff", 0.08) : alpha("#000", 0.08),
          }}
        >
          <FormControl size="small" sx={{ minWidth: 260, maxWidth: 380 }}>
            <InputLabel id="tables-occupancy-date-label">Journée</InputLabel>
            <Select
              labelId="tables-occupancy-date-label"
              value={availableDates.includes(selectedDate) ? selectedDate : ""}
              label="Journée"
              onChange={(e) => setSelectedDate(e.target.value)}
              disabled={availableDates.length === 0}
            >
              {availableDates.map((date) => (
                <MenuItem key={date} value={date}>
                  {formatDayLabel(date)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {isTimelineView && (
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              flexWrap="wrap"
              useFlexGap
            >
              <LegendSwatch color={colorTermine} label="Terminé" />
              <LegendSwatch color={colorEncours} label="En cours" />
              {viewMode !== "board" && (
                <LegendSwatch
                  color={colorTermine}
                  threePlayers
                  accent={colorThreeAccent}
                  label="3 joueurs"
                />
              )}
              {viewMode === "board" && (
                <LegendSwatch
                  color={colorTermine}
                  threePlayers
                  accent={colorThreeAccent}
                  label="Tour qui chevauche"
                />
              )}
              <Typography variant="caption" color="text.secondary">
                {viewMode === "table"
                  ? "Libellé = tableau·tour (P = poules)"
                  : viewMode === "boardTour"
                    ? "Libellé = n° de table · voies si matchs en parallèle"
                    : "Bloc = tour · voies + rayures si chevauchement"}
              </Typography>
              <Tooltip title="Clic sur un bloc = ouvrir dans Gérer les matchs">
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <OpenInNewIcon sx={{ fontSize: 14, color: "text.secondary" }} />
                  <Typography variant="caption" color="text.secondary">
                    Lien Matchs
                  </Typography>
                </Stack>
              </Tooltip>
            </Stack>
          )}

          {isTimelineView && (
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Tooltip title="Zoom arrière (Ctrl + molette)">
                <span>
                  <IconButton
                    size="small"
                    onClick={() => handleZoom(zoom - ZOOM_STEP)}
                    disabled={zoom <= MIN_ZOOM}
                    aria-label="Zoom arrière"
                  >
                    <ZoomOutIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Slider
                size="small"
                value={zoom}
                min={MIN_ZOOM}
                max={MAX_ZOOM}
                step={ZOOM_STEP}
                onChange={(_, value) =>
                  handleZoom(Array.isArray(value) ? value[0] : value)
                }
                sx={{ width: 120, mx: 0.5 }}
                aria-label="Niveau de zoom"
              />
              <Tooltip title="Zoom avant (Ctrl + molette)">
                <span>
                  <IconButton
                    size="small"
                    onClick={() => handleZoom(zoom + ZOOM_STEP)}
                    disabled={zoom >= MAX_ZOOM}
                    aria-label="Zoom avant"
                  >
                    <ZoomInIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Recentrer sur la période des matchs">
                <IconButton
                  size="small"
                  onClick={handleResetFocus}
                  aria-label="Recentrer"
                >
                  <CenterFocusStrongIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                  fontVariantNumeric: "tabular-nums",
                  fontWeight: 600,
                  minWidth: 92,
                }}
              >
                {formatTime(new Date(activeRange.startMs))}–
                {formatTime(new Date(activeRange.endMs))}
              </Typography>
            </Stack>
          )}
        </Stack>
      )}

      {viewMode === "now" && (
        <TablesOccupancyNow
          tables={tables}
          liveBlocks={liveNowBlocks}
          now={now}
          estimatedMatchDurationMinutes={
            tournoi?.estimatedMatchDurationMinutes
          }
        />
      )}

      {viewMode === "heatmap" && (
        <TablesOccupancyHeatmap
          blocks={allBlocks}
          tableCount={tables.length}
          rangeStartMs={activeRange.startMs}
          rangeEndMs={activeRange.endMs}
        />
      )}

      {viewMode === "table" && tables.length === 0 ? (
        <Typography color="text.secondary">
          Aucune table configurée.
        </Typography>
      ) : viewMode === "boardTour" && boardTourGroups.length === 0 ? (
        <Typography color="text.secondary">
          Aucun match lancé pour cette journée.
        </Typography>
      ) : viewMode === "board" && boardRows.length === 0 ? (
        <Typography color="text.secondary">
          Aucun match lancé pour cette journée.
        </Typography>
      ) : isTimelineView ? (
        <Box
          ref={scrollRef}
          onWheel={handleWheelZoom}
          sx={{
            borderRadius: 2.5,
            overflow: "auto",
            bgcolor: trackBg,
            border: 1,
            borderColor: isDark ? alpha("#fff", 0.1) : alpha("#000", 0.1),
            boxShadow: isDark
              ? `0 8px 28px ${alpha("#000", 0.45)}`
              : `0 8px 24px ${alpha("#0B1F33", 0.08)}`,
            maxHeight: "calc(100vh - 280px)",
          }}
        >
          <Box sx={{ minWidth: labelWidthPx + timelineWidthPx }}>
            {/* En-tête horaires */}
            <Box
              sx={{
                display: "flex",
                position: "sticky",
                top: 0,
                zIndex: 4,
                bgcolor: headerBg,
                borderBottom: `1px solid ${gridLineStrong}`,
              }}
            >
              <Box
                sx={{
                  width: labelWidthPx,
                  flexShrink: 0,
                  px: 1.25,
                  py: 1.15,
                  position: "sticky",
                  left: 0,
                  zIndex: 5,
                  bgcolor: headerBg,
                  borderRight: `1px solid ${gridLineStrong}`,
                  boxShadow: isDark
                    ? `6px 0 14px ${alpha("#000", 0.35)}`
                    : `6px 0 12px ${alpha("#0B1F33", 0.08)}`,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: isDark ? alpha("#fff", 0.7) : alpha("#0B1F33", 0.65),
                    fontWeight: 800,
                    textTransform: "uppercase",
                    letterSpacing: 0.8,
                    fontSize: 10,
                  }}
                >
                  {viewMode === "table"
                    ? "Table"
                    : viewMode === "boardTour"
                      ? "Tour"
                      : "Tableau"}
                </Typography>
              </Box>
              <Box
                sx={{
                  position: "relative",
                  width: timelineWidthPx,
                  height: 42,
                  zIndex: 0,
                  isolation: "isolate",
                }}
              >
                {hourTicks.map((tickMs) => {
                  const leftPx =
                    ((tickMs - activeRange.startMs) / rangeDurationMs) *
                    timelineWidthPx;
                  const hour = new Date(tickMs).getHours();
                  return (
                    <Box
                      key={tickMs}
                      sx={{
                        position: "absolute",
                        left: leftPx,
                        top: 0,
                        bottom: 0,
                        borderLeft: `1px solid ${gridLineStrong}`,
                        pl: 1,
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          color: isDark ? "#E8EEF6" : "#1A2A3A",
                          fontWeight: 800,
                          fontVariantNumeric: "tabular-nums",
                          fontSize: 12,
                        }}
                      >
                        {hour}h
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Box>

            {viewMode === "table" &&
              tableGroups.map((group) => (
                <Box key={group.key}>
                  <Box
                    sx={{
                      position: "sticky",
                      top: 42,
                      zIndex: 3,
                      width: labelWidthPx + timelineWidthPx,
                      bgcolor: groupBg,
                      borderBottom: `1px solid ${gridLineStrong}`,
                      py: 0.7,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        position: "sticky",
                        left: 0,
                        display: "inline-block",
                        zIndex: 4,
                        bgcolor: groupBg,
                        pl: 1.5,
                        pr: 2,
                        fontWeight: 800,
                        color: isDark ? "#F2F6FB" : "#152033",
                        letterSpacing: 0.2,
                        fontSize: 12,
                        whiteSpace: "nowrap",
                        boxShadow: `8px 0 12px ${alpha(groupBg, 1)}`,
                      }}
                    >
                      {group.label}
                    </Typography>
                  </Box>

                  {group.tables.map((table, rowIndex) => {
                    const blocks = occupancyByTable.get(table.tableId) ?? [];
                    const rowBg = rowIndex % 2 === 0 ? rowEvenBg : rowOddBg;
                    const hasBlocks = blocks.length > 0;

                    return (
                      <Box
                        key={table.tableId}
                        sx={{
                          display: "flex",
                          borderBottom: `1px solid ${gridLine}`,
                          bgcolor: rowBg,
                          height: ROW_HEIGHT_PX,
                          transition: "background-color 0.15s ease",
                          "&:hover": {
                            bgcolor: isDark
                              ? alpha(colorTermine, 0.1)
                              : alpha(colorTermine, 0.07),
                          },
                        }}
                      >
                        <Box
                          sx={{
                            width: labelWidthPx,
                            flexShrink: 0,
                            px: 1.25,
                            display: "flex",
                            alignItems: "center",
                            position: "sticky",
                            left: 0,
                            zIndex: 3,
                            bgcolor: labelColBg,
                            borderRight: `1px solid ${gridLineStrong}`,
                            boxShadow: isDark
                              ? `6px 0 14px ${alpha("#000", 0.35)}`
                              : `6px 0 12px ${alpha("#0B1F33", 0.08)}`,
                          }}
                        >
                          <Typography
                            variant="body2"
                            noWrap
                            sx={{
                              color: hasBlocks
                                ? isDark
                                  ? "#F4F7FB"
                                  : "#152033"
                                : isDark
                                  ? alpha("#F4F7FB", 0.45)
                                  : alpha("#152033", 0.4),
                              fontWeight: 700,
                              fontVariantNumeric: "tabular-nums",
                              fontSize: 13,
                            }}
                          >
                            {table.tableId}
                          </Typography>
                        </Box>

                        <Box
                          sx={{
                            position: "relative",
                            width: timelineWidthPx,
                            height: ROW_HEIGHT_PX,
                            flexShrink: 0,
                            zIndex: 0,
                            isolation: "isolate",
                            opacity: hasBlocks ? 1 : 0.45,
                          }}
                        >
                          {hourTicks.map((tickMs) => {
                            const leftPx =
                              ((tickMs - activeRange.startMs) /
                                rangeDurationMs) *
                              timelineWidthPx;
                            return (
                              <Box
                                key={tickMs}
                                sx={{
                                  position: "absolute",
                                  left: leftPx,
                                  top: 0,
                                  bottom: 0,
                                  width: 0,
                                  borderLeft: `1px solid ${gridLine}`,
                                }}
                              />
                            );
                          })}

                          {nowPercent != null && (
                            <Box
                              sx={{
                                position: "absolute",
                                left: `${nowPercent}%`,
                                top: 0,
                                bottom: 0,
                                width: 2,
                                bgcolor: theme.palette.error.main,
                                opacity: 0.75,
                                zIndex: 2,
                                pointerEvents: "none",
                              }}
                            />
                          )}

                          {blocks.map(({ match, start, end }) => {
                            const left = percentBetween(
                              start.getTime(),
                              activeRange.startMs,
                              activeRange.endMs
                            );
                            const right = percentBetween(
                              end.getTime(),
                              activeRange.startMs,
                              activeRange.endMs
                            );
                            const widthPct = Math.max(right - left, 0.45);
                            const board = boardsById.get(
                              String(match.tableauId)
                            );
                            const tour = roundsById.get(String(match.tourId));
                            const boardLabel = board
                              ? `${board.name}${
                                  board.label ? ` · ${board.label}` : ""
                                }`
                              : "Tableau inconnu";
                            const tourLabel = tour?.label ?? "Tour inconnu";
                            const isLive = match.status === "ENCOURS";
                            const isThreePlayers = playerCount(match) >= 3;
                            const barColor = isLive
                              ? colorEncours
                              : colorTermine;
                            const innerLabel = barInnerLabel(
                              board?.name,
                              tourLabel
                            );

                            return (
                              <Tooltip
                                key={String(match.id)}
                                arrow
                                placement="top"
                                enterDelay={100}
                                describeChild
                                title={
                                  <MatchTooltipContent
                                    match={match}
                                    start={start}
                                    end={end}
                                    boardLabel={boardLabel}
                                    tourLabel={tourLabel}
                                  />
                                }
                              >
                                <Box
                                  role="button"
                                  tabIndex={0}
                                  aria-label={`Match table ${table.tableId}, tableau ${board?.name ?? "?"}, ${tourLabel}, ${formatTime(start)} à ${formatTime(end)}. Ouvrir dans Gérer les matchs`}
                                  onClick={() => openMatchInAdmin(match)}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                      e.preventDefault();
                                      openMatchInAdmin(match);
                                    }
                                  }}
                                  sx={{
                                    position: "absolute",
                                    left: `${left}%`,
                                    width: `${widthPct}%`,
                                    top: 6,
                                    height: ROW_HEIGHT_PX - 12,
                                    bgcolor: barColor,
                                    borderRadius: 1.25,
                                    cursor: "pointer",
                                    zIndex: 1,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "flex-start",
                                    pl: isThreePlayers ? 1 : 0.75,
                                    pr: 0.5,
                                    overflow: "hidden",
                                    boxShadow: isLive
                                      ? `0 0 0 1px ${alpha(colorEncours, 0.45)}, 0 2px 10px ${alpha(colorEncours, 0.35)}`
                                      : `0 1px 3px ${alpha("#000", 0.28)}`,
                                    transition:
                                      "transform 0.12s ease, filter 0.12s ease, box-shadow 0.12s ease",
                                    ...(isThreePlayers
                                      ? {
                                          "&::before": {
                                            content: '""',
                                            position: "absolute",
                                            left: 0,
                                            top: 0,
                                            bottom: 0,
                                            width: 2.5,
                                            bgcolor: colorThreeAccent,
                                          },
                                        }
                                      : {}),
                                    "&:hover, &:focus-visible": {
                                      transform: "translateY(-1px)",
                                      filter: "brightness(1.08)",
                                      bgcolor: isLive
                                        ? colorEncours
                                        : colorHover,
                                      outline: "none",
                                      zIndex: 1,
                                      boxShadow: `0 2px 10px ${alpha(barColor, 0.45)}`,
                                    },
                                  }}
                                >
                                  <Typography
                                    component="span"
                                    sx={{
                                      color: barLabelColor,
                                      fontWeight: 800,
                                      fontSize: 11.5,
                                      lineHeight: 1,
                                      letterSpacing: 0.15,
                                      whiteSpace: "nowrap",
                                      fontVariantNumeric: "tabular-nums",
                                      textShadow: `0 1px 1px ${alpha("#000", 0.4)}`,
                                    }}
                                  >
                                    {innerLabel}
                                  </Typography>
                                </Box>
                              </Tooltip>
                            );
                          })}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              ))}

            {viewMode === "boardTour" &&
              boardTourGroups.map((group) => (
                <Box key={group.key}>
                  <Box
                    sx={{
                      position: "sticky",
                      top: 42,
                      zIndex: 3,
                      width: labelWidthPx + timelineWidthPx,
                      bgcolor: groupBg,
                      borderBottom: `1px solid ${gridLineStrong}`,
                      py: 0.7,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        position: "sticky",
                        left: 0,
                        display: "inline-block",
                        zIndex: 4,
                        bgcolor: groupBg,
                        pl: 1.5,
                        pr: 2,
                        fontWeight: 800,
                        color: isDark ? "#F2F6FB" : "#152033",
                        letterSpacing: 0.2,
                        fontSize: 12,
                        whiteSpace: "nowrap",
                        boxShadow: `8px 0 12px ${alpha(groupBg, 1)}`,
                      }}
                    >
                      {group.label}
                    </Typography>
                  </Box>

                  {group.rows.map((row, rowIndex) => {
                    const rowHeight = Math.max(
                      ROW_HEIGHT_PX,
                      8 + row.laneCount * LANE_HEIGHT_PX
                    );
                    const rowBg = rowIndex % 2 === 0 ? rowEvenBg : rowOddBg;
                    const tourCode = shortTourCode(row.tourLabel);

                    return (
                      <Box
                        key={row.key}
                        sx={{
                          display: "flex",
                          borderBottom: `1px solid ${gridLine}`,
                          bgcolor: rowBg,
                          height: rowHeight,
                          transition: "background-color 0.15s ease",
                          "&:hover": {
                            bgcolor: isDark
                              ? alpha(colorTermine, 0.1)
                              : alpha(colorTermine, 0.07),
                          },
                        }}
                      >
                        <Box
                          sx={{
                            width: labelWidthPx,
                            flexShrink: 0,
                            px: 1.25,
                            display: "flex",
                            alignItems: "center",
                            position: "sticky",
                            left: 0,
                            zIndex: 3,
                            bgcolor: labelColBg,
                            borderRight: `1px solid ${gridLineStrong}`,
                            boxShadow: isDark
                              ? `6px 0 14px ${alpha("#000", 0.35)}`
                              : `6px 0 12px ${alpha("#0B1F33", 0.08)}`,
                          }}
                        >
                          <Typography
                            variant="body2"
                            noWrap
                            title={row.tourLabel}
                            sx={{
                              color: isDark ? "#F4F7FB" : "#152033",
                              fontWeight: 700,
                              fontSize: 13,
                            }}
                          >
                            {row.boardName}·{tourCode}
                          </Typography>
                        </Box>

                        <Box
                          sx={{
                            position: "relative",
                            width: timelineWidthPx,
                            height: rowHeight,
                            flexShrink: 0,
                            zIndex: 0,
                            isolation: "isolate",
                          }}
                        >
                          {hourTicks.map((tickMs) => {
                            const leftPx =
                              ((tickMs - activeRange.startMs) /
                                rangeDurationMs) *
                              timelineWidthPx;
                            return (
                              <Box
                                key={tickMs}
                                sx={{
                                  position: "absolute",
                                  left: leftPx,
                                  top: 0,
                                  bottom: 0,
                                  width: 0,
                                  borderLeft: `1px solid ${gridLine}`,
                                }}
                              />
                            );
                          })}

                          {nowPercent != null && (
                            <Box
                              sx={{
                                position: "absolute",
                                left: `${nowPercent}%`,
                                top: 0,
                                bottom: 0,
                                width: 2,
                                bgcolor: theme.palette.error.main,
                                opacity: 0.75,
                                zIndex: 2,
                                pointerEvents: "none",
                              }}
                            />
                          )}

                          {row.blocks.map(({ match, start, end, lane }) => {
                            const left = percentBetween(
                              start.getTime(),
                              activeRange.startMs,
                              activeRange.endMs
                            );
                            const right = percentBetween(
                              end.getTime(),
                              activeRange.startMs,
                              activeRange.endMs
                            );
                            const widthPct = Math.max(right - left, 0.45);
                            const board = boardsById.get(
                              String(match.tableauId)
                            );
                            const tour = roundsById.get(String(match.tourId));
                            const boardLabel = board
                              ? `${board.name}${
                                  board.label ? ` · ${board.label}` : ""
                                }`
                              : "Tableau inconnu";
                            const tourLabel = tour?.label ?? "Tour inconnu";
                            const isLive = match.status === "ENCOURS";
                            const isThreePlayers = playerCount(match) >= 3;
                            const barColor = isLive
                              ? colorEncours
                              : colorTermine;
                            const barTop = 4 + lane * LANE_HEIGHT_PX;
                            const barHeight = LANE_HEIGHT_PX - 8;
                            const tableLabel =
                              match.tableId != null
                                ? `T${match.tableId}`
                                : "?";

                            return (
                              <Tooltip
                                key={String(match.id)}
                                arrow
                                placement="top"
                                enterDelay={100}
                                describeChild
                                title={
                                  <MatchTooltipContent
                                    match={match}
                                    start={start}
                                    end={end}
                                    boardLabel={boardLabel}
                                    tourLabel={tourLabel}
                                  />
                                }
                              >
                                <Box
                                  role="button"
                                  tabIndex={0}
                                  aria-label={`${boardLabel}, ${tourLabel}, table ${match.tableId ?? "?"}, ${formatTime(start)} à ${formatTime(end)}. Ouvrir dans Gérer les matchs`}
                                  onClick={() => openMatchInAdmin(match)}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                      e.preventDefault();
                                      openMatchInAdmin(match);
                                    }
                                  }}
                                  sx={{
                                    position: "absolute",
                                    left: `${left}%`,
                                    width: `${widthPct}%`,
                                    top: barTop,
                                    height: barHeight,
                                    bgcolor: barColor,
                                    borderRadius: 1.25,
                                    cursor: "pointer",
                                    zIndex: 1,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "flex-start",
                                    pl: isThreePlayers ? 1 : 0.75,
                                    pr: 0.5,
                                    overflow: "hidden",
                                    boxShadow: isLive
                                      ? `0 0 0 1px ${alpha(colorEncours, 0.45)}, 0 2px 10px ${alpha(colorEncours, 0.35)}`
                                      : `0 1px 3px ${alpha("#000", 0.28)}`,
                                    transition:
                                      "transform 0.12s ease, filter 0.12s ease, box-shadow 0.12s ease",
                                    ...(isThreePlayers
                                      ? {
                                          "&::before": {
                                            content: '""',
                                            position: "absolute",
                                            left: 0,
                                            top: 0,
                                            bottom: 0,
                                            width: 2.5,
                                            bgcolor: colorThreeAccent,
                                          },
                                        }
                                      : {}),
                                    "&:hover, &:focus-visible": {
                                      transform: "translateY(-1px)",
                                      filter: "brightness(1.08)",
                                      bgcolor: isLive
                                        ? colorEncours
                                        : colorHover,
                                      outline: "none",
                                      zIndex: 1,
                                      boxShadow: `0 2px 10px ${alpha(barColor, 0.45)}`,
                                    },
                                  }}
                                >
                                  <Typography
                                    component="span"
                                    sx={{
                                      color: barLabelColor,
                                      fontWeight: 800,
                                      fontSize: 11,
                                      lineHeight: 1,
                                      letterSpacing: 0.15,
                                      whiteSpace: "nowrap",
                                      fontVariantNumeric: "tabular-nums",
                                      textShadow: `0 1px 1px ${alpha("#000", 0.4)}`,
                                    }}
                                  >
                                    {tableLabel}
                                  </Typography>
                                </Box>
                              </Tooltip>
                            );
                          })}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              ))}

            {viewMode === "board" &&
              boardRows.map((row, rowIndex) => {
                const rowBg = rowIndex % 2 === 0 ? rowEvenBg : rowOddBg;
                const rowHeight = Math.max(
                  ROW_HEIGHT_PX,
                  8 + row.laneCount * LANE_HEIGHT_PX
                );

                return (
                  <Box
                    key={row.key}
                    sx={{
                      display: "flex",
                      borderBottom: `1px solid ${gridLine}`,
                      bgcolor: rowBg,
                      height: rowHeight,
                      transition: "background-color 0.15s ease",
                      "&:hover": {
                        bgcolor: isDark
                          ? alpha(colorTermine, 0.1)
                          : alpha(colorTermine, 0.07),
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: labelWidthPx,
                        flexShrink: 0,
                        px: 1.25,
                        display: "flex",
                        alignItems: "center",
                        position: "sticky",
                        left: 0,
                        zIndex: 3,
                        bgcolor: labelColBg,
                        borderRight: `1px solid ${gridLineStrong}`,
                        boxShadow: isDark
                          ? `6px 0 14px ${alpha("#000", 0.35)}`
                          : `6px 0 12px ${alpha("#0B1F33", 0.08)}`,
                      }}
                    >
                      <Typography
                        variant="body2"
                        noWrap
                        title={row.boardLabel}
                        sx={{
                          color: isDark ? "#F4F7FB" : "#152033",
                          fontWeight: 700,
                          fontSize: 13,
                        }}
                      >
                        {row.boardName}
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        position: "relative",
                        width: timelineWidthPx,
                        height: rowHeight,
                        flexShrink: 0,
                        zIndex: 0,
                        isolation: "isolate",
                      }}
                    >
                      {hourTicks.map((tickMs) => {
                        const leftPx =
                          ((tickMs - activeRange.startMs) / rangeDurationMs) *
                          timelineWidthPx;
                        return (
                          <Box
                            key={tickMs}
                            sx={{
                              position: "absolute",
                              left: leftPx,
                              top: 0,
                              bottom: 0,
                              width: 0,
                              borderLeft: `1px solid ${gridLine}`,
                            }}
                          />
                        );
                      })}

                      {nowPercent != null && (
                        <Box
                          sx={{
                            position: "absolute",
                            left: `${nowPercent}%`,
                            top: 0,
                            bottom: 0,
                            width: 2,
                            bgcolor: theme.palette.error.main,
                            opacity: 0.75,
                            zIndex: 2,
                            pointerEvents: "none",
                          }}
                        />
                      )}

                      {row.tours.map((tour) => {
                        const left = percentBetween(
                          tour.start.getTime(),
                          activeRange.startMs,
                          activeRange.endMs
                        );
                        const right = percentBetween(
                          tour.end.getTime(),
                          activeRange.startMs,
                          activeRange.endMs
                        );
                        const widthPct = Math.max(right - left, 0.6);
                        const isLive = tour.liveCount > 0;
                        const barColor = isLive ? colorEncours : colorTermine;
                        const tourCode = shortTourCode(tour.tourLabel);
                        const barTop = 4 + tour.lane * LANE_HEIGHT_PX;
                        const barHeight = LANE_HEIGHT_PX - 8;
                        const innerLabel =
                          widthPct * timelineWidthPx > 72
                            ? `${tourCode} · ${tour.matchCount}`
                            : tourCode;

                        return (
                          <Tooltip
                            key={tour.tourId}
                            arrow
                            placement="top"
                            enterDelay={100}
                            describeChild
                            title={
                              <TourSpanTooltipContent
                                boardLabel={row.boardLabel}
                                tour={tour}
                              />
                            }
                          >
                            <Box
                              role="button"
                              tabIndex={0}
                              aria-label={`Tableau ${row.boardName}, ${tour.tourLabel}, ${tour.matchCount} matchs${tour.overlaps ? ", chevauchement" : ""}, ${formatTime(tour.start)} à ${formatTime(tour.end)}. Ouvrir dans Gérer les matchs`}
                              onClick={() =>
                                openBoardTourInAdmin(row.boardId, tour.tourId)
                              }
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  openBoardTourInAdmin(
                                    row.boardId,
                                    tour.tourId
                                  );
                                }
                              }}
                              sx={{
                                position: "absolute",
                                left: `${left}%`,
                                width: `${widthPct}%`,
                                top: barTop,
                                height: barHeight,
                                bgcolor: barColor,
                                borderRadius: 1.25,
                                cursor: "pointer",
                                zIndex: tour.overlaps ? 2 : 1,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "flex-start",
                                pl: tour.overlaps ? 1 : 0.75,
                                pr: 0.5,
                                overflow: "hidden",
                                boxShadow: tour.overlaps
                                  ? `0 0 0 2px ${colorThreeAccent}, 0 2px 10px ${alpha(colorThreeAccent, 0.45)}`
                                  : isLive
                                    ? `0 0 0 1px ${alpha(colorEncours, 0.45)}, 0 2px 10px ${alpha(colorEncours, 0.35)}`
                                    : `0 1px 3px ${alpha("#000", 0.28)}`,
                                backgroundImage: tour.overlaps
                                  ? `repeating-linear-gradient(
                                      -45deg,
                                      transparent,
                                      transparent 4px,
                                      ${alpha("#000", 0.22)} 4px,
                                      ${alpha("#000", 0.22)} 8px
                                    )`
                                  : undefined,
                                transition:
                                  "transform 0.12s ease, filter 0.12s ease, box-shadow 0.12s ease",
                                ...(tour.overlaps
                                  ? {
                                      "&::before": {
                                        content: '""',
                                        position: "absolute",
                                        left: 0,
                                        top: 0,
                                        bottom: 0,
                                        width: 3,
                                        bgcolor: colorThreeAccent,
                                      },
                                    }
                                  : {}),
                                "&:hover, &:focus-visible": {
                                  transform: "translateY(-1px)",
                                  filter: "brightness(1.08)",
                                  bgcolor: isLive ? colorEncours : colorHover,
                                  outline: "none",
                                  zIndex: 3,
                                  boxShadow: tour.overlaps
                                    ? `0 0 0 2px ${colorThreeAccent}, 0 2px 12px ${alpha(colorThreeAccent, 0.55)}`
                                    : `0 2px 10px ${alpha(barColor, 0.45)}`,
                                },
                              }}
                            >
                              <Typography
                                component="span"
                                sx={{
                                  color: barLabelColor,
                                  fontWeight: 800,
                                  fontSize: 11.5,
                                  lineHeight: 1,
                                  letterSpacing: 0.15,
                                  whiteSpace: "nowrap",
                                  fontVariantNumeric: "tabular-nums",
                                  textShadow: `0 1px 1px ${alpha("#000", 0.4)}`,
                                }}
                              >
                                {innerLabel}
                                {tour.overlaps ? " ↯" : ""}
                              </Typography>
                            </Box>
                          </Tooltip>
                        );
                      })}
                    </Box>
                  </Box>
                );
              })}
          </Box>
        </Box>
      ) : null}

      {!loading &&
        isTimelineView &&
        allBlocks.length === 0 &&
        tables.length > 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Aucun match lancé sur une table pour cette journée.
          </Typography>
        )}
    </Box>
  );
};

export default TablesOccupancy;
